import {
	t,
	Trans,
} from "@lingui/macro"
import {
	mdiChevronDoubleRight,
	mdiEmoticonExcited,
	mdiEmoticonNeutral,
	mdiEmoticonSad,
} from "@mdi/js"
import Icon from "@mdi/react"
import {
	AlertColor,
	Box,
	Button,
	Chip,
	Divider,
	Grid,
	MenuItem,
	Popover,
	Select,
	Typography,
} from "@mui/material"
import {
	ErrorResponse,
	MatesDatePicker,
	MatesMultilineTextField,
	MatesSelect,
	MatesTextField,
	RestResponse,
	withUseFormHook,
} from "@sinossi/mates-react-library"
import { lightTheme } from "commons/themes"
import { SbSnackbar } from "components/commons/sb-snackbar"
import TemplateSelectionModal from "components/commons/template-selection-modal"
import { UpdateProposalRequest } from "components/match-proposal/model"
import {
	MatesComponent,
	MatesComponentState,
} from "components/mates-component"
import { NewStep } from "components/workflow/modals"
import { ScheduleVisitModal } from "components/visit/modals"
import { DateTime } from "luxon"
import {
	AccountType,
	AssetMatchBrand,
	AssetMatchesBrandRejectReason,
	AssetMatchesBrandRejectReasonTranslations,
	AssetMatchesBrandStatus,
	ContractType,
	CreateSpecificWorkflow,
	NewStepRequest,
	PriorityLevel,
	SessionResponse,
	WorkflowObjectType,
} from "models"
import { enqueueSnackbar } from "notistack"
import React, { ReactElement } from "react"
import { UseFormReturn } from "react-hook-form"
import { ActionsServices } from "services/actions.services"
import { MatchService } from "services/match.service"
import withUseRouter, { UseRouterParams } from "services/router"
import { WorkflowService } from "services/workflow.service"
import MatchProposalDetailsFormValidation, { MatchProposalDetailsFormModel } from "./form"

interface MatchProposalDetailsFormProps extends UseFormReturn, UseRouterParams {
	match: AssetMatchBrand
	refreshData(): void
	sessionData?: SessionResponse
}

interface MatchProposalDetailsFormState extends MatesComponentState {
	openActions: boolean
	openWorkflow: boolean
	openReminder: boolean
	openScheduleVisit: boolean
	selectedStatus: AssetMatchesBrandStatus
	snackbarData: {
		openNotification: boolean,
		notificationMessage: string,
		severity: AlertColor
	}
}

const statusMap = {
	[AssetMatchesBrandStatus.CREATED]: {
		label: <Trans id="enums.asset-matches-brand-status.CREATED">Creato</Trans>,
		color: "#adb5bd"
	},
	[AssetMatchesBrandStatus.APPROVED]: {
		label: <Trans id="enums.asset-matches-brand-status.APPROVED">Approvato</Trans>,
		color: "success.light"
	},
	[AssetMatchesBrandStatus.INSPECTION]: {
		label: <Trans id="enums.asset-matches-brand-status.INSPECTION">Sopralluogo</Trans>,
		color: "primary.main"
	},
	[AssetMatchesBrandStatus.INTERESTED]: {
		label: <Trans id="enums.asset-matches-brand-status.INTERESTED">Interessato</Trans>,
		color: "secondary.main"
	},
	[AssetMatchesBrandStatus.SENT]: {
		label: <Trans id="enums.asset-matches-brand-status.SENT">Inviato</Trans>,
		color: "#ef832e"
	},
	[AssetMatchesBrandStatus.REJECTED]: {
		label: <Trans id="enums.asset-matches-brand-status.REJECTED">Rifiutato</Trans>,
		color: "#000000"
	},
	[AssetMatchesBrandStatus.CONTRACT_DONE]: {
		label: <Trans id="enums.asset-matches-brand-status.CONTRACT_DONE">Contrattualizzata</Trans>,
		color: "success.light"
	},
};

class MatchProposalDetailsForm extends React.Component<MatchProposalDetailsFormProps, MatchProposalDetailsFormState> {
	private readonly actionButtonRef: React.RefObject<any> = React.createRef()
	private readonly matesComponent: MatesComponent<MatchProposalDetailsFormProps, MatchProposalDetailsFormState> = new MatesComponent<MatchProposalDetailsFormProps, MatchProposalDetailsFormState>(this)

	constructor(props: MatchProposalDetailsFormProps) {
		super(props)
		this.state = {
			...this.matesComponent.setupState(),
			openActions: false,
			openWorkflow: false,
			openReminder: false,
			openScheduleVisit: false,
			selectedStatus: this.props.match.status,
			snackbarData: {
				openNotification: false,
				notificationMessage: "",
				severity: "info"
			}
		}

		const { match } = this.props
		let form = new MatchProposalDetailsFormModel()
		form.brand = match.brand.name
		form.company = match.brand.company.name
		form.representative = match.brand.representatives.length ? match.brand.representatives[0].name : ""
		form.category = match.brand.products.map(product => product.name).join(", ")
		form.contractType = match.proposta.type
		form.price = match.proposta.price.toFixed(2)
		form.notes = match.notes
		form.sentDate = DateTime.fromISO(match.proposta.creationDate)
		form.expireDate = DateTime.fromISO(match.proposta.expirationDate)
		form.paymentTerms = match.proposta.paymentTerms
		form.compatibility = match.compatibility
		form.status = match.status
		form.priority = match.priorityMatch

		this.props.reset(form)
	}

	private togglePriority = () => {
		const levels = Object.values(PriorityLevel);
		const current = this.props.watch("priority") ?? this.props.match.priorityMatch;
		const currentIndex = levels.indexOf(current);
		const nextIndex = (currentIndex + 1) % levels.length;
		const nextPriority = levels[nextIndex];
		this.props.setValue("priority", nextPriority);
	}

	private getPriority = (): ReactElement => {
		const currentPriority = this.props.watch("priority") ?? this.props.match.priorityMatch;

		const chipProps = {
			onClick: this.togglePriority,
			sx: { cursor: "pointer", color: "black", fontWeight: 500 }
		};

		switch (currentPriority) {
			case PriorityLevel.URGENTE:
			case PriorityLevel.ALTA:
				return <Chip label={currentPriority} color="error" {...chipProps} />;
			case PriorityLevel.NORMALE:
				return <Chip
					label={currentPriority}
					sx={[{ backgroundColor: "#ef832e" }, chipProps.sx]}
					onClick={chipProps.onClick}
				/>;
			case PriorityLevel.BASSA:
				return <Chip
					label={currentPriority}
					sx={[{ backgroundColor: "#fef022" }, chipProps.sx]}
					onClick={chipProps.onClick}
				/>;
			default:
				return <Chip label="?" {...chipProps} />;
		}
	}


	private getCompatibility = (): ReactElement => {
		const value = this.props.watch("compatibility") ?? this.props.match.compatibility;

		return (
			<Box sx={{ display: "flex", gap: 1 }}>
				<Box onClick={() => this.props.setValue("compatibility", 100)} sx={{ cursor: "pointer" }}>
					<Icon
						path={mdiEmoticonExcited}
						size={1}
						color={value > 66 ? lightTheme.palette.info.main : "#adb5bd"}
					/>
				</Box>
				<Box onClick={() => this.props.setValue("compatibility", 66)} sx={{ cursor: "pointer" }}>
					<Icon
						path={mdiEmoticonNeutral}
						size={1}
						color={value <= 66 && value > 33 ? lightTheme.palette.info.main : "#adb5bd"}
					/>
				</Box>
				<Box onClick={() => this.props.setValue("compatibility", 33)} sx={{ cursor: "pointer" }}>
					<Icon
						path={mdiEmoticonSad}
						size={1}
						color={value <= 33 ? lightTheme.palette.info.main : "#adb5bd"}
					/>
				</Box>
			</Box>
		);
	}

	private renderRejectInfo = (): ReactElement | null => {
		if (this.state.selectedStatus !== AssetMatchesBrandStatus.REJECTED) {
			return null
		}

		const { rejectReason, rejectDescription } = this.props.match

		if (!rejectReason && !(rejectDescription && rejectDescription.trim().length)) {
			return null
		}

		let translatedReason: string | null = null
		if (rejectReason) {
			const translations = new AssetMatchesBrandRejectReasonTranslations()
			const key = rejectReason as keyof AssetMatchesBrandRejectReasonTranslations
			translatedReason = translations[key]
		}

		const trimmedDescription = rejectDescription?.trim() ?? ""

		return (
			<Box
				sx={{
					mt: 2,
					mb: 2,
					p: 2,
					borderRadius: 1,
					border: "1px solid #f5c2c7",
					backgroundColor: "#fdecea",
					color: "#842029",
				}}
			>
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					<Trans id="mpdf.reject-info">Motivazione del rifiuto</Trans>
				</Typography>
				{translatedReason && (
					<Box sx={{ mb: trimmedDescription ? 1 : 0 }}>
						<Typography variant="caption" sx={{ fontWeight: 600 }}>
							<Trans id="mpdf.reject-info.reason-label">Motivo</Trans>
						</Typography>
						<Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
							{translatedReason}
						</Typography>
					</Box>
				)}
				{trimmedDescription && (
					<Box>
						<Typography variant="caption" sx={{ fontWeight: 600 }}>
							<Trans id="mpdf.reject-info.description-label">Descrizione</Trans>
						</Typography>
						<Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
							{trimmedDescription}
						</Typography>
					</Box>
				)}
			</Box>
		)
	}

	private getStatus = (): ReactElement => {
		switch (this.props.match.status) {
			case AssetMatchesBrandStatus.CREATED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.CREATED"}>
								Creato
							</Trans>
						}
						sx={{ backgroundColor: "#adb5bd", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.APPROVED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.APPROVED"}>
								Approvato
							</Trans>
						}
						sx={{ backgroundColor: "success.light", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.INSPECTION:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.INSPECTION"}>
								Sopralluogo
							</Trans>
						}
						sx={{ backgroundColor: "primary.main", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.INTERESTED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.INTERESTED"}>
								Interessato
							</Trans>
						}
						sx={{ backgroundColor: "secondary.main", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.SENT:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.SENT"}>
								Inviato
							</Trans>
						}
						sx={{ backgroundColor: "#ef832e", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.REJECTED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.REJECTED"}>
								Rifiutato
							</Trans>
						}
						sx={{ backgroundColor: "#000000", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.CONTRACT_DONE:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.CONTRACT_DONE"}>
								Contrattualizzata
							</Trans>
						}
						sx={{ backgroundColor: "success.light", textTransform: "uppercase", color: "white" }}
					/>
				)
		}
	}

	private getContractTypeLabel = (type: ContractType): string => {
		switch(type) {
		case ContractType.BUY:
			return t({ id: "enums.contract-type.BUY", message: "Acquisto" })
		case ContractType.LEASE:
			return t({ id: "enums.contract-type.LEASE", message: "Affitto" })
		case ContractType.BRANCH_LEASE:
			return t({ id: "enums.contract-type.BRANCH_LEASE", message: "Affitto di ramo d'azienda" })
		case ContractType.TEMPORARY_LEASE:
			return t({ id: "enums.contract-type.TEMPORARY_LEASE", message: "Locazione temporanea" })
		default:
			return t({ id: "enums.contract-type.undefined", message: type })
		}
	}

	private createWorkflow = (templateId: string) => {
		if (this.props.match.asset) {
			const request: CreateSpecificWorkflow = {
				objectId: this.props.match.id,
				objectType: WorkflowObjectType.MATCH,
				workflowTemplateId: templateId
			}
			WorkflowService.createWorkflowFromSpecificObject(request, this.createWorkflowCallback, this.matesComponent.onError)
		}
	}

	private createWorkflowCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.openSnackbar(
				t({ id: "asset-form.create-workflow.error", message: "Qualcosa è andato storto, riprova tra qualche istante." }),
				"error"
			)
			this.matesComponent.onError(response)
			return
		}
		this.openSnackbar(
			t({ id: "asset-form.create-workflow.success", message: "Workflow creato con successo." }),
			"success"
		)
	}

	private createReminder = (request: NewStepRequest) => {
		ActionsServices.createNewAction(request, this.createReminderCallback, this.matesComponent.onError)
	}

	private createReminderCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			this.openSnackbar(
				t({ id: "asset-form.create-reminder.error", message: "Qualcosa è andato storto, riprova tra qualche istante." }),
				"error"
			)
			this.setState(() => ({
				openReminder: false
			}))
			return
		}

		this.openSnackbar(
			t({ id: "asset-form.create-reminder.success", message: "Promemoria creato con successo." }),
			"success"
		)
		this.setState(() => ({
			openReminder: false
		}))
	}

	private handleScheduleVisitSuccess = (message: string) => {
		this.openSnackbar(message, "success")
		this.setState(() => ({ openScheduleVisit: false }))
	}

	private handleScheduleVisitError = (_?: any) => {
		this.openSnackbar(
			t({ id: "schedule-visit.error", message: "Errore durante la programmazione del sopralluogo" }),
			"error"
		)
		this.setState(() => ({ openScheduleVisit: false }))
	}

	private openSnackbar = (message: string, severity: AlertColor) => {
		this.setState(() => ({
			snackbarData: {
				openNotification: true,
				notificationMessage: message,
				severity: severity
			}
		}))
	}

	private closeSnackbar = () => {
		this.setState((prevState) => ({
			snackbarData: {
				...prevState.snackbarData,
				openNotification: false
			}
		}))
	}

	private updateMatchProposal = async () => {
		const values = this.props.getValues();

		const payload: UpdateProposalRequest = {
			type: values.contractType,
			expirationDate: values.expireDate,
			creationDate: values.sentDate,
			price: parseFloat(values.price),
			message: values.notes,
			compatibility: values.compatibility,
			status: values.status,
			priority: values.priority,
			paymentTerms: values.paymentTerms
		};


		const assetId = this.props.match.asset.id;
		const brandId = this.props.match.brand.id;

		MatchService.updateProposal(payload, assetId, brandId, this.onUpdateSuccessful, this.matesComponent.onError)
	}

	private onUpdateSuccessful = (resp: RestResponse<any, ErrorResponse>) => {
		if (resp.hasError()) {
			this.matesComponent.onException(resp)
			enqueueSnackbar(
				t({ id: "match-update.error", message: "Qualcosa è andato storto, riprova tra qualche istante." }),
				{ variant: "error" }
			)
			return
		}
		enqueueSnackbar(
			t({ id: "match-update.success", message: "Proposta aggiornata" }),
			{ variant: "success" }
		)

		this.props.refreshData()
	}

	private makeMatchVisibleToBrand = () => {
		MatchService.makeMatchVisibleToBrand(
			this.props.match.id,
			this.makeMatchVisibleToBrandCallback,
			this.matesComponent.onException
		)
	}

	private makeMatchVisibleToBrandCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			this.openSnackbar(
				t({ id: "mpdf.make-visible.error", message: "Errore nel rendere il match visibile al brand" }),
				"error"
			)
			return
		}

		this.openSnackbar(
			t({ id: "mpdf.make-visible.success", message: "Match reso visibile al brand con successo" }),
			"success"
		)
		this.props.refreshData()
	}


	render() {
		return (
			<Box
				sx={{
					display: "flex",
					height: `calc(100% - 62px)`,
					gap: 4,
					p: 4
				}}
			>
				<Box
					sx={{
						width: "60%",
						height: "100%"
					}}
				>
					<Box
						sx={{ height: `calc(100% - 60px)`, boxSizing: "border-box" }}
					>
						<Grid container spacing={4}>
							<Grid item sm={12} md={6}>
								<MatesTextField
									disabled
									variant={'outlined'}
									label={t({
										id: "mpdf.brand",
										message: "Inviato a",
									})}
									name="brand"
									control={this.props.control}
									trigger={this.props.trigger}
									formState={this.props.formState}
								/>
							</Grid>
							<Grid item sm={12} md={6}>
								<MatesTextField
									disabled
									variant={'outlined'}
									label={t({
										id: "mpdf.company",
										message: "Azienda",
									})}
									name="company"
									control={this.props.control}
									trigger={this.props.trigger}
									formState={this.props.formState}
								/>
							</Grid>
							<Grid item sm={12} md={6}>
								<MatesTextField
									disabled
									variant={'outlined'}
									label={t({
										id: "mpdf.category",
										message: "Categoria merceologica",
									})}
									name="category"
									control={this.props.control}
									trigger={this.props.trigger}
									formState={this.props.formState}
								/>
							</Grid>
							<Grid item sm={12} md={6}>
								<MatesTextField
									disabled
									variant={'outlined'}
									label={t({
										id: "mpdf.representative",
										message: "Referente",
									})}
									name="representative"
									control={this.props.control}
									trigger={this.props.trigger}
									formState={this.props.formState}
								/>
							</Grid>
							<Grid item sm={12}>
								<Divider />
							</Grid>
							<Grid item sm={12} md={6}>
								<MatesDatePicker
									variant="outlined"
									label={t({
										id: "mpdf.sent-date",
										message: "Data proposta",
									})}
									name="sentDate"
									control={this.props.control}
									trigger={this.props.trigger}
									formState={this.props.formState}
									maxDate={DateTime.fromISO(new Date().toISOString())}
								/>
							</Grid>
							<Grid item sm={12} md={6}>
								<MatesDatePicker
									variant="outlined"
									label={t({
										id: "mpdf.expire-date",
										message: "Data scadenza",
									})}
									name="expireDate"
									control={this.props.control}
									trigger={this.props.trigger}
									formState={this.props.formState}
									minDate={DateTime.fromISO(new Date().toISOString())}
								/>
							</Grid>
							<Grid item sm={12} md={6}>
								<MatesSelect
									variant={"outlined"}
									label={t({
										id: "mpdf.contract-type",
										message: "Tipologia contratto",
									}
									)}
									name="contractType"
									control={this.props.control}
									trigger={this.props.trigger}
									formState={this.props.formState}
								>
									{
										(Object.values(ContractType) as ContractType[]).map(type => (
											<option key={type} value={type}>
												{this.getContractTypeLabel(type)}
											</option>
										))
									}
								</MatesSelect>
							</Grid>
							<Grid item sm={12} md={6}>
								<MatesTextField
									variant={'outlined'}
									label={t({
										id: "mpdf.price",
										message: "Importo proposto",
									})}
									name="price"
									endAdornment={"€"}
									control={this.props.control}
									trigger={this.props.trigger}
									formState={this.props.formState}
								/>
							</Grid>
						</Grid>
					</Box>
					<Box
						sx={{
							width: "100%",
							height: "34px",
							display: "flex",
							justifyContent: "right",
						}}
					>
						<Button
							variant={"contained"}
							color={"info"}
							sx={{ px: 1, fontSize: "0.8rem" }}
							onClick={async () => {
								const valid = await this.props.trigger();
								if (valid) {
									this.updateMatchProposal();
								}
							}}
						>
							<Trans id={"mpdf.update-match-details"}>Aggiorna proposta</Trans>
						</Button>
					</Box>
				</Box>
				<Box>
					<Divider orientation={"vertical"} />
				</Box>
				<Box
					sx={{
						width: "35%",
						height: `calc(100% - 32px)`,
						mt: 0,
						display: "flex",
						flexDirection: "column"
					}}
				>
					{/* Sezione superiore con Priorità e Stato */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							mb: 2
						}}
					>
						<Box>
							<Typography variant="subtitle2" color="textSecondary">
								<Trans id={"mpdf.priority"}>
									Priorità
								</Trans>
							</Typography>
							{this.getPriority()}
						</Box>
						<Box>
							<Typography variant="subtitle2" color="textSecondary">
								<Trans id="mpdf.status">Stato</Trans>
							</Typography>
							<Select
								value={this.state.selectedStatus}
								variant="standard"
								disableUnderline
								onChange={(e) => {
									const status = e.target.value as AssetMatchesBrandStatus;
									this.setState({ selectedStatus: status });
									this.props.setValue("status", status); // sincronizza col form
								}}
								renderValue={(value) => {
									const status = statusMap[value];
									return (
										<Chip
											label={status.label}
											sx={{
												backgroundColor: status.color,
												textTransform: "uppercase",
												color: "white"
											}}
										/>
									);
								}}
								fullWidth
							>
								{Object.entries(statusMap).map(([key, { label, color }]) => (
									<MenuItem key={key} value={key}>
										<Chip
											label={label}
											sx={{
												backgroundColor: color,
												textTransform: "uppercase",
												color: "white"
											}}
										/>
									</MenuItem>
								))}
							</Select>
						</Box>
					</Box>

					{/* Sezione Compatibilità */}
					<Box sx={{ mb: 2 }}>
						<Typography variant="subtitle2" color="textSecondary">
							<Trans id={"mpdf.compatibility"}>
								Compatibilità
							</Trans>
						</Typography>
						{this.getCompatibility()}
					</Box>

					{this.renderRejectInfo()}

					{/* Pulsante per rendere il match visibile al brand */}
					{this.props.sessionData?.accountType === AccountType.ADMIN &&
					 this.state.selectedStatus === AssetMatchesBrandStatus.INTERESTED &&
					 !this.props.match.visible && (
						<Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
							<Button
								variant="contained"
								color="secondary"
								sx={{ color: "white" }}
								onClick={this.makeMatchVisibleToBrand}
							>
								<Trans id="mpdf.make-visible-button">Rendi visibile al brand</Trans>
							</Button>
						</Box>
					)}

					{/* Campo Note - occupa lo spazio rimanente */}
					<Box
						sx={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							minHeight: 0, // Importante per il flex
							mb: 2
						}}
					>
						<MatesMultilineTextField
							variant={"outlined"}
							label={t({
								id: "mpdf.notes",
								message: "Note",
							})}
							name="notes"
							maxRows={20}
							minRows={8}
							control={this.props.control}
							trigger={this.props.trigger}
							formState={this.props.formState}
							maxLength={15000}
							sx={{
								flex: 1,
								"& .MuiInputBase-root": {
									height: "100%",
									alignItems: "flex-start"
								},
								"& .MuiInputBase-input": {
									height: "100% !important",
									overflow: "auto !important"
								}
							}}
						/>
					</Box>

					{/* Pulsante Azioni rapide - sempre in fondo */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "right",
							mt: "auto"
						}}
					>
						<Button
							variant={"contained"}
							color={"info"}
							startIcon={<Icon path={mdiChevronDoubleRight} size={1} />}
							sx={{
								px: 1,
								fontSize: "0.8rem",
							}}
							ref={this.actionButtonRef}
							onClick={() => this.setState(() => ({ openActions: true }))}
						>
							<Trans id={"dms-fast-actions"}>Azioni rapide</Trans>
						</Button>
					</Box>
				</Box>
				<Popover
					anchorEl={this.actionButtonRef.current}
					open={this.state.openActions}
					onClose={() => this.setState(() => ({ openActions: false }))}
					anchorOrigin={{
						vertical: "center",
						horizontal: "left",
					}}
					transformOrigin={{
						vertical: "center",
						horizontal: "right",
					}}
					slotProps={{
						paper: {
							sx: {
								p: 2,
								overflow: "hidden",
								width: "200px"
							},
						},
					}}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "start",
							gap: 2,
						}}
					>
						<Box
							sx={{
								cursor: "pointer"
							}}
							onClick={() => {
								this.setState(() => ({ openActions: false }))
								this.props.navigate(`/match-proposal/asset/${this.props.match.asset.id}`)
							}}
						>
							<Trans id={"dms-fast-actions.sent-match"}>Invia proposta</Trans>
						</Box>
						<Box
							sx={{
								cursor: "pointer"
							}}
							onClick={() => this.setState(() => ({ openReminder: true, openActions: false }))}
						>
							<Trans id={"dms-fast-actions.reminder"}>Crea promemoria</Trans>
						</Box>
						<Box
							sx={{
								cursor: "pointer"
							}}
							onClick={() => this.setState(() => ({ openWorkflow: true, openActions: false }))}
						>
							<Trans id={"dms-fast-actions.workflow"}>Scatena workflow</Trans>
						</Box>
						<Box
							sx={{
								cursor: "pointer"
							}}
							onClick={() => this.setState(() => ({
								openScheduleVisit: true,
								openActions: false,
							}))}
						>
							<Trans id={"dms-fast-actions.schedule-visit"}>Programma sopralluogo</Trans>
						</Box>
					</Box>
				</Popover>

		{
			this.state.openWorkflow &&
				<TemplateSelectionModal
					templateType={WorkflowObjectType.MATCH}
					onClose={() => this.setState(() => ({ openWorkflow: false }))}
					submit={this.createWorkflow}
				/>
		}
		{
			this.state.openReminder &&
				<NewStep
					openDialog
					onClose={() => this.setState(() => ({ openReminder: false }))}
					submit={this.createReminder}
					action
					create
				/>
		}
		{
			this.state.openScheduleVisit &&
				<ScheduleVisitModal
					open={this.state.openScheduleVisit}
					onClose={() => this.setState(() => ({ openScheduleVisit: false }))}
					assetId={this.props.match.asset.id}
					matchId={this.props.match.id}
					onSuccess={this.handleScheduleVisitSuccess}
					onError={this.handleScheduleVisitError}
				/>
		}
		<SbSnackbar snackbarData={this.state.snackbarData} handleCloseNotification={this.closeSnackbar} />
			</Box >
		)
	}
}

export default withUseRouter(withUseFormHook<{}>(
	MatchProposalDetailsForm,
	MatchProposalDetailsFormValidation,
	{}
))
